/**
 * Idioma del MERCADO al que pertenece un país: la lengua que se puede dar por
 * supuesta en alguien que navega desde ahí.
 *
 * No es «el idioma del usuario» —eso lo declara su navegador— sino un contrapeso
 * a esa declaración. `navigator.languages` no es el idioma del sistema ni el de
 * la interfaz: es la lista «Idiomas preferidos» del navegador, que muchísima
 * gente nunca configura y que se queda en el default de instalación (a menudo
 * `en-US`). Sin contrapeso, a un padre en España con Chrome recién instalado le
 * ofrecíamos la versión inglesa de una página que lee perfectamente.
 *
 * Misma señal y mismo razonamiento que `display-currency.ts` (ADR-020/021): el
 * país viene del GeoIP del edge, que describe la petición, y llega al cliente
 * por la cookie `pn_country` sin volver dinámicas las landings.
 *
 * Dominio puro a propósito: la política se prueba sin React ni jsdom.
 */

import { parseCountryCodes, type CountryCode } from '@/server/domain/country'

/**
 * Los mercados con idioma presupuesto. Se declara aquí y no se importa
 * `PublicLocale` de `lib/` para que la capa domain no dependa de nada fuera de
 * sí misma — y de paso el tipo dice la verdad: `en` nunca sale de este módulo
 * (ver `marketLanguageForCountry`).
 */
export type MarketLanguage = 'es' | 'pt' | 'it' | 'de'

/**
 * Hispanohablantes: los 20 países de lengua oficial española más Puerto Rico y
 * Guinea Ecuatorial. `ReadonlySet<CountryCode>` y no `string[]` por lo mismo que
 * en `display-currency`: `has()` rechaza que le pases un idioma, y la
 * pertenencia es un hash. Un código mal escrito desaparece en silencio al
 * parsear, de eso avisa el test que fija el tamaño.
 */
export const SPANISH_MARKET_COUNTRIES: ReadonlySet<CountryCode> = new Set(
  parseCountryCodes(
    'ES,MX,AR,CO,CL,PE,VE,EC,BO,PY,UY,CR,PA,DO,GT,HN,NI,SV,CU,PR,GQ'
  )
)

/** Lusófonos: los ocho de la CPLP. */
export const PORTUGUESE_MARKET_COUNTRIES: ReadonlySet<CountryCode> = new Set(
  parseCountryCodes('PT,BR,AO,MZ,CV,GW,ST,TL')
)

/**
 * Italianos: Italia, San Marino y la Ciudad del Vaticano. Suiza queda fuera
 * a propósito: el italiano es solo una de sus cuatro lenguas oficiales, y
 * presuponerlo para todo visitante suizo contradiría al alemán o al francófono
 * que declaró su preferencia en el navegador.
 */
export const ITALIAN_MARKET_COUNTRIES: ReadonlySet<CountryCode> = new Set(
  parseCountryCodes('IT,SM,VA')
)

/**
 * Germanófonos inequívocos: Alemania, Austria y Liechtenstein. Suiza queda
 * fuera por la misma razón que en el mapa italiano: su mercado es multilingüe
 * y el país por sí solo no permite contradecir la preferencia del navegador.
 */
export const GERMAN_MARKET_COUNTRIES: ReadonlySet<CountryCode> = new Set(
  parseCountryCodes('DE,AT,LI')
)

/**
 * Idioma que se puede presuponer en el país, o `null` si no lo sabemos.
 *
 * **Solo cubre `es`, `pt`, `it` y `de`, no `en`, y la asimetría es deliberada.** El
 * sesgo que corregimos es que el default del navegador (`en-US`) contradiga un
 * mercado no anglófono. En un país anglófono ese default *coincide* con el
 * mercado, así que una preferencia declarada distinta del inglés ahí es una
 * decisión consciente del usuario —típicamente un hispanohablante en EE. UU.
 * que puso su navegador en español— y esa sí merece que se le ofrezca su
 * idioma. Meter `US,GB` en un mapa de vetos le quitaría precisamente a esa
 * persona la versión que pidió.
 */
export function marketLanguageForCountry(country: CountryCode | null): MarketLanguage | null {
  if (country === null) return null
  if (SPANISH_MARKET_COUNTRIES.has(country)) return 'es'
  if (PORTUGUESE_MARKET_COUNTRIES.has(country)) return 'pt'
  if (ITALIAN_MARKET_COUNTRIES.has(country)) return 'it'
  if (GERMAN_MARKET_COUNTRIES.has(country)) return 'de'
  return null
}
